import React, { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Send, Mail, MapPin, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AnimatedText from './AnimatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  'UGC Creation',
  'Social Media Management',
  'Online Presence',
  'Brand Building',
  'Social Media Marketing',
  'Website Development',
  'App Development',
  'Other',
];

const InfoTile = ({ Icon, label, value, href, delay = 0, isInView }) => {
  const Wrap = href ? motion.a : motion.div;
  return (
    <Wrap
      href={href}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -4 }}
      data-cursor-label={href ? 'Email' : undefined}
      className="group relative p-6 bg-white/70 backdrop-blur-sm border border-[#1C1917]/8 rounded-2xl overflow-hidden"
    >
      {/* Hover lavender wash */}
      <div className="absolute inset-0 bg-[#AA94FF]/0 group-hover:bg-[#AA94FF]/10 transition-colors duration-500" />
      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#1C1917] flex items-center justify-center group-hover:bg-[#AA94FF] transition-colors">
          <Icon className="w-5 h-5 text-[#AA94FF] group-hover:text-[#1C1917] transition-colors" />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#78716C] font-bold mb-1">
            {label}
          </p>
          <p className="font-semibold text-[#1C1917] text-base leading-tight">{value}</p>
        </div>
      </div>
    </Wrap>
  );
};

const Field = ({ children, focused }) => (
  <div className="relative">
    {children}
    <motion.span
      aria-hidden="true"
      animate={{ scaleX: focused ? 1 : 0 }}
      transition={{ duration: 0.4, ease: [0.2, 0.9, 0.25, 1] }}
      className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#AA94FF] origin-left"
    />
  </div>
);

const Contact = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focus, setFocus] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service: '', message: '',
  });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const blobY = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  const wordX = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      toast.error('Please fill in all required fields (Name, Email, Phone, Service)');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.status === 'success') {
        toast.success("Thank you! We'll be in touch soon.");
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      }
    } catch (err) {
      console.error('Contact form error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full bg-transparent border-b border-[#1C1917]/15 py-4 text-base sm:text-lg text-[#1C1917] placeholder:text-[#78716C]/70 focus:outline-none focus:border-transparent transition-colors';

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={sectionRef}
      className="relative py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3] overflow-hidden"
    >
      {/* Background blobs */}
      <motion.div
        aria-hidden="true"
        style={{ y: blobY }}
        className="pointer-events-none absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#AA94FF]/25 blur-3xl"
      />
      <motion.div
        aria-hidden="true"
        style={{ y: blobY }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute -bottom-40 -right-20 w-[520px] h-[520px] rounded-full bg-[#FFA8F2]/25 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 sm:mb-20">
          <div className="lg:col-span-4">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#AA94FF] mb-4">
              Get in touch
            </p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#1C1917] text-[#F4F4F3] rounded-full px-3 py-1.5"
            >
              <Sparkles className="w-3 h-3 text-[#AA94FF]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                Reply in 48h
              </span>
            </motion.div>
          </div>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-5xl sm:text-6xl lg:text-8xl font-black tracking-[-0.03em] leading-[0.95] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={60}
              stagger={0.06}
            >
              Let's build something
            </AnimatedText>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="block text-5xl sm:text-6xl lg:text-8xl italic font-light tracking-[-0.02em] leading-[0.95] text-[#AA94FF] mt-2"
              style={{ fontFamily: "'Cabinet Grotesk', serif" }}
            >
              worth watching.
            </motion.span>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* LEFT — Info tiles */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <InfoTile
              Icon={Mail}
              label="Email"
              value="hello@hypelabmarketing.co.uk"
              href="mailto:hello@hypelabmarketing.co.uk"
              delay={0.1}
              isInView={isInView}
            />
            <InfoTile
              Icon={MapPin}
              label="Based in"
              value="London, United Kingdom"
              delay={0.2}
              isInView={isInView}
            />
            <InfoTile
              Icon={Clock}
              label="Availability"
              value="Mon – Fri · 9am–6pm GMT"
              delay={0.3}
              isInView={isInView}
            />

            {/* Quote card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative overflow-hidden mt-4 p-8 rounded-3xl bg-[#1C1917] text-[#F4F4F3]"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#AA94FF]/30 blur-3xl" />
              <p className="relative text-2xl sm:text-3xl font-black leading-tight tracking-[-0.02em] mb-3"
                 style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Not a fit for everyone.
              </p>
              <p className="relative text-sm text-[#F4F4F3]/70 leading-relaxed">
                I take on 2 new brands per quarter to keep the work personal. If we're a match,
                you'll hear back within 48 hours with a proposed call.
              </p>
            </motion.div>
          </div>

          {/* RIGHT — Form */}
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white/70 backdrop-blur-sm rounded-3xl p-8 sm:p-12 border border-[#1C1917]/8"
          >
            <div className="space-y-2">
              <Field focused={focus === 'name'}>
                <input
                  type="text" name="name" value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocus('name')} onBlur={() => setFocus('')}
                  placeholder="Your name *" required
                  data-testid="contact-name-input"
                  className={inputCls}
                />
              </Field>
              <Field focused={focus === 'email'}>
                <input
                  type="email" name="email" value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocus('email')} onBlur={() => setFocus('')}
                  placeholder="Email *" required
                  data-testid="contact-email-input"
                  className={inputCls}
                />
              </Field>
              <Field focused={focus === 'phone'}>
                <input
                  type="tel" name="phone" value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setFocus('phone')} onBlur={() => setFocus('')}
                  placeholder="Phone *" required
                  data-testid="contact-phone-input"
                  className={inputCls}
                />
              </Field>
              <Field focused={focus === 'company'}>
                <input
                  type="text" name="company" value={formData.company}
                  onChange={handleChange}
                  onFocus={() => setFocus('company')} onBlur={() => setFocus('')}
                  placeholder="Company (optional)"
                  data-testid="contact-company-input"
                  className={inputCls}
                />
              </Field>

              {/* Service chips */}
              <div className="pt-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#78716C] font-bold mb-3">
                  What service are you after? *
                </p>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map((s) => {
                    const active = formData.service === s;
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setFormData((p) => ({ ...p, service: s }))}
                        data-testid={`service-chip-${s.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full border transition-all ${
                          active
                            ? 'bg-[#1C1917] text-[#F4F4F3] border-[#1C1917]'
                            : 'bg-transparent text-[#1C1917] border-[#1C1917]/25 hover:border-[#1C1917]'
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field focused={focus === 'message'}>
                <textarea
                  name="message" value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocus('message')} onBlur={() => setFocus('')}
                  placeholder="Tell me about your project (optional)"
                  rows={3}
                  data-testid="contact-message-input"
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </div>

            {/* Submit */}
            <motion.button
              type="submit" disabled={isSubmitting}
              data-testid="contact-submit-button"
              data-cursor-label="Send"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group mt-10 inline-flex items-center gap-4 bg-[#1C1917] text-[#F4F4F3] rounded-full pl-7 pr-2 py-2 hover:bg-[#AA94FF] hover:text-[#1C1917] transition-colors disabled:opacity-60"
            >
              <span className="text-sm font-bold uppercase tracking-[0.15em]">
                {isSubmitting ? 'Sending...' : 'Send message'}
              </span>
              <span className="w-12 h-12 rounded-full bg-[#AA94FF] group-hover:bg-[#1C1917] flex items-center justify-center transition-colors">
                {isSubmitting ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                    <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <ArrowRight className="w-5 h-5 text-[#1C1917] group-hover:text-[#F4F4F3] group-hover:-rotate-45 transition-all duration-300" />
                )}
              </span>
            </motion.button>
          </motion.form>
        </div>

        {/* Bottom giant wordmark */}
        <motion.p
          aria-hidden="true"
          style={{ x: wordX, fontFamily: "'Cabinet Grotesk', sans-serif" }}
          className="mt-24 sm:mt-32 text-[18vw] lg:text-[14rem] font-black tracking-[-0.06em] leading-none text-[#1C1917]/[0.05] whitespace-nowrap select-none pointer-events-none"
        >
          LET'S TALK · LET'S TALK
        </motion.p>
      </div>
    </section>
  );
};

export default Contact;
